import React, { useState, useEffect } from 'react';
import {
  Save,
  TestTube,
  Power,
  PowerOff,
  CreditCard,
  Truck,
  Eye,
  EyeOff,
  CheckCircle,
  AlertCircle,
  RefreshCw,
  Clock,
  Shield,
  X
} from 'lucide-react';
import { API_BASE_URL } from '../../config';

const API = ({ isSuperAdmin }) => {
  const [apiSettings, setApiSettings] = useState({
    razorpay: {
      configured: false,
      enabled: false,
      test_mode: true,
      last_verified: null,
      key_id_masked: '',
      key_secret_masked: ''
    },
    shiprocket: {
      configured: false,
      enabled: false,
      last_verified: null,
      token_expires_at: null,
      email: '',
      password_masked: ''
    }
  });

  const [formData, setFormData] = useState({
    razorpay: {
      key_id: '',
      key_secret: '',
      test_mode: true,
      is_enabled: false
    },
    shiprocket: {
      email: '',
      password: '',
      is_enabled: false
    }
  });

  const [loading, setLoading] = useState({
    fetch: false,
    razorpay: false,
    shiprocket: false,
    toggle: {},
    test: {}
  });

  const [showPasswords, setShowPasswords] = useState({
    razorpay_secret: false,
    shiprocket_password: false
  });

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');


  const getAuthHeaders = () => {
    const token = localStorage.getItem('authToken');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  };

  // Load API settings on component mount
  useEffect(() => {
    if (!isSuperAdmin) loadApiSettings();
  }, [isSuperAdmin]);

  const loadApiSettings = async () => {
    setLoading(prev => ({ ...prev, fetch: true }));
    setError('');

    try {
      const response = await fetch(`${API_BASE_URL}/admin/api-settings`, {
        headers: getAuthHeaders()
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setApiSettings(data.data);

        // Pre-fill form data with existing values
        setFormData({
          razorpay: {
            key_id: '',
            key_secret: '',
            test_mode: data.data.razorpay.test_mode,
            is_enabled: data.data.razorpay.enabled
          },
          shiprocket: {
            email: data.data.shiprocket.email || '',
            password: '',
            is_enabled: data.data.shiprocket.enabled
          }
        });
      } else {
        setError(data.error || 'Failed to load API settings');
      }
    } catch (err) {
      setError('Network error. Please try again.');
      console.error('Load API settings error:', err);
    }

    setLoading(prev => ({ ...prev, fetch: false }));
  };

  const handleInputChange = (provider, field, value) => {
    setFormData(prev => ({
      ...prev,
      [provider]: {
        ...prev[provider],
        [field]: value
      }
    }));
    setError('');
  };

  const saveRazorpayConfig = async () => {
    const { key_id, key_secret, test_mode, is_enabled } = formData.razorpay;

    if (!key_id.trim() || !key_secret.trim()) {
      setError('Please enter both Razorpay Key ID and Key Secret');
      return;
    }

    setLoading(prev => ({ ...prev, razorpay: true }));
    setError('');
    setSuccess('');

    try {
      const response = await fetch(`${API_BASE_URL}/admin/api-settings/razorpay`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          key_id: key_id.trim(),
          key_secret: key_secret.trim(),
          test_mode,
          is_enabled
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSuccess('Razorpay configuration saved successfully!');
        setApiSettings(prev => ({
          ...prev,
          razorpay: data.data
        }));

        // Clear form
        setFormData(prev => ({
          ...prev,
          razorpay: {
            ...prev.razorpay,
            key_id: '',
            key_secret: ''
          }
        }));
      } else {
        setError(data.error || 'Failed to save Razorpay configuration');
      }
    } catch (err) {
      setError('Network error. Please try again.');
      console.error('Save Razorpay error:', err);
    }

    setLoading(prev => ({ ...prev, razorpay: false }));
  };

  const saveShiprocketConfig = async () => {
    const { email, password, is_enabled } = formData.shiprocket;

    if (!email.trim() || !password.trim()) {
      setError('Please enter both Shiprocket email and password');
      return;
    }

    setLoading(prev => ({ ...prev, shiprocket: true }));
    setError('');
    setSuccess('');

    try {
      const response = await fetch(`${API_BASE_URL}/admin/api-settings/shiprocket`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          email: email.trim(),
          password: password.trim(),
          is_enabled
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSuccess('Shiprocket configuration saved successfully!');
        setApiSettings(prev => ({
          ...prev,
          shiprocket: data.data
        }));

        // Clear password field
        setFormData(prev => ({
          ...prev,
          shiprocket: {
            ...prev.shiprocket,
            password: ''
          }
        }));
      } else {
        setError(data.error || 'Failed to save Shiprocket configuration');
      }
    } catch (err) {
      setError('Network error. Please try again.');
      console.error('Save Shiprocket error:', err);
    }

    setLoading(prev => ({ ...prev, shiprocket: false }));
  };

  const toggleProvider = async (provider, enabled) => {
    setLoading(prev => ({
      ...prev,
      toggle: { ...prev.toggle, [provider]: true }
    }));

    try {
      const response = await fetch(`${API_BASE_URL}/admin/api-settings/${provider}/toggle`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ is_enabled: enabled })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSuccess(`${provider.charAt(0).toUpperCase() + provider.slice(1)} ${enabled ? 'enabled' : 'disabled'} successfully!`);
        setApiSettings(prev => ({
          ...prev,
          [provider]: {
            ...prev[provider],
            enabled: enabled
          }
        }));
      } else {
        setError(data.error || `Failed to ${enabled ? 'enable' : 'disable'} ${provider}`);
      }
    } catch (err) {
      setError('Network error. Please try again.');
    }

    setLoading(prev => ({
      ...prev,
      toggle: { ...prev.toggle, [provider]: false }
    }));
  };

  const testConnection = async (provider) => {
    setLoading(prev => ({
      ...prev,
      test: { ...prev.test, [provider]: true }
    }));

    try {
      const response = await fetch(`${API_BASE_URL}/admin/api-settings/${provider}/test-connection`, {
        method: 'POST',
        headers: getAuthHeaders()
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSuccess(`${provider.charAt(0).toUpperCase() + provider.slice(1)} connection test successful!`);
        setApiSettings(prev => ({
          ...prev,
          [provider]: {
            ...prev[provider],
            last_verified: data.data.last_verified
          }
        }));
      } else {
        setError(data.error || `${provider} connection test failed`);
      }
    } catch (err) {
      setError('Network error. Please try again.');
    }

    setLoading(prev => ({
      ...prev,
      test: { ...prev.test, [provider]: false }
    }));
  };

  const createTestTransaction = async () => {
    setLoading(prev => ({
      ...prev,
      test: { ...prev.test, 'razorpay-transaction': true }
    }));

    try {
      const response = await fetch(`${API_BASE_URL}/admin/api-settings/razorpay/test-transaction`, {
        method: 'POST',
        headers: getAuthHeaders()
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSuccess(`Test transaction created! Order ID: ${data.data.order_id}`);
      } else {
        setError(data.error || 'Test transaction failed');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    }

    setLoading(prev => ({
      ...prev,
      test: { ...prev.test, 'razorpay-transaction': false }
    }));
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  const isTokenExpiringSoon = (expiryDate) => {
    if (!expiryDate) return false;
    const expiry = new Date(expiryDate);
    const now = new Date();
    const daysLeft = (expiry - now) / (1000 * 60 * 60 * 24);
    return daysLeft < 3;
  };

  if (isSuperAdmin) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">API Settings</h1>
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-amber-800">
          <p>Store-level settings. Log in as a provider to manage a store&apos;s API settings.</p>
        </div>
      </div>
    );
  }

  if (loading.fetch) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <p className="ml-2 text-gray-600">Loading API settings...</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">API Settings</h1>
        <p className="text-gray-600">Configure and manage your payment and shipping integrations</p>
      </div>

      {/* Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 flex items-center">
          <AlertCircle className="h-5 w-5 text-red-500 mr-2 flex-shrink-0" />
          <span className="text-red-700 text-sm">{error}</span>
          <button
            onClick={() => setError('')}
            className="ml-auto text-red-500 hover:text-red-700"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 rounded-md p-4 flex items-center">
          <CheckCircle className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
          <span className="text-green-700 text-sm">{success}</span>
          <button
            onClick={() => setSuccess('')}
            className="ml-auto text-green-500 hover:text-green-700"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Razorpay Configuration */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="p-6 border-b">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <CreditCard className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Razorpay</h2>
                <p className="text-sm text-gray-600">Payment gateway configuration</p>
              </div>
            </div>

            <div className="flex items-center space-x-4">
              {/* Status Badge */}
              <div className="flex items-center space-x-2">
                {apiSettings.razorpay.configured ? (
                  <div className="flex items-center space-x-1">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span className="text-sm text-green-600 font-medium">Connected</span>
                  </div>
                ) : (
                  <div className="flex items-center space-x-1">
                    <AlertCircle className="h-4 w-4 text-red-500" />
                    <span className="text-sm text-red-600 font-medium">Not Connected</span>
                  </div>
                )}
              </div>

              {/* Enable/Disable Toggle */}
              {apiSettings.razorpay.configured && (
                <button
                  onClick={() => toggleProvider('razorpay', !apiSettings.razorpay.enabled)}
                  disabled={loading.toggle.razorpay}
                  className={`flex items-center space-x-2 px-3 py-1 rounded-md text-sm font-medium transition-colors ${apiSettings.razorpay.enabled
                    ? 'bg-green-100 text-green-700 hover:bg-green-200'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    } disabled:opacity-50`}
                >
                  {loading.toggle.razorpay ? (
                    <RefreshCw className="h-3 w-3 animate-spin" />
                  ) : apiSettings.razorpay.enabled ? (
                    <Power className="h-3 w-3" />
                  ) : (
                    <PowerOff className="h-3 w-3" />
                  )}
                  <span>{apiSettings.razorpay.enabled ? 'Enabled' : 'Disabled'}</span>
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="p-6 space-y-4">
          {/* Current Configuration Display */}
          {apiSettings.razorpay.configured && (
            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-700">Key ID:</span>
                <span className="text-sm text-gray-900 font-mono">{apiSettings.razorpay.key_id_masked}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-700">Secret:</span>
                <span className="text-sm text-gray-900 font-mono">{apiSettings.razorpay.key_secret_masked}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-700">Mode:</span>
                <span className={`text-sm font-medium ${apiSettings.razorpay.test_mode ? 'text-yellow-600' : 'text-green-600'
                  }`}>
                  {apiSettings.razorpay.test_mode ? 'Test Mode' : 'Live Mode'}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-700">Last Verified:</span>
                <span className="text-sm text-gray-600">{formatDateTime(apiSettings.razorpay.last_verified)}</span>
              </div>
            </div>
          )}

          {/* Configuration Form */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Razorpay Key ID *
              </label>
              <input
                type="text"
                value={formData.razorpay.key_id}
                onChange={(e) => handleInputChange('razorpay', 'key_id', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="rzp_test_..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Razorpay Key Secret *
              </label>
              <div className="relative">
                <input
                  type={showPasswords.razorpay_secret ? 'text' : 'password'}
                  value={formData.razorpay.key_secret}
                  onChange={(e) => handleInputChange('razorpay', 'key_secret', e.target.value)}
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Your secret key"
                />
                <button
                  type="button"
                  onClick={() => setShowPasswords(prev => ({
                    ...prev,
                    razorpay_secret: !prev.razorpay_secret
                  }))}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPasswords.razorpay_secret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
          </div>

          {/* Options */}
          <div className="flex items-center space-x-6">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.razorpay.test_mode}
                onChange={(e) => handleInputChange('razorpay', 'test_mode', e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mr-2"
              />
              <span className="text-sm text-gray-700">Test Mode</span>
            </label>

            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.razorpay.is_enabled}
                onChange={(e) => handleInputChange('razorpay', 'is_enabled', e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mr-2"
              />
              <span className="text-sm text-gray-700">Enable after saving</span>
            </label>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-3">
            <button
              onClick={saveRazorpayConfig}
              disabled={loading.razorpay}
              className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading.razorpay ? (
                <RefreshCw className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              <span>Save Configuration</span>
            </button>

            {apiSettings.razorpay.configured && (
              <>
                <button
                  onClick={() => testConnection('razorpay')}
                  disabled={loading.test.razorpay}
                  className="flex items-center space-x-2 bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 disabled:opacity-50"
                >
                  {loading.test.razorpay ? (
                    <RefreshCw className="h-4 w-4 animate-spin" />
                  ) : (
                    <TestTube className="h-4 w-4" />
                  )}
                  <span>Test Connection</span>
                </button>

                <button
                  onClick={createTestTransaction}
                  disabled={loading.test['razorpay-transaction']}
                  className="flex items-center space-x-2 bg-purple-600 text-white px-4 py-2 rounded-md hover:bg-purple-700 disabled:opacity-50"
                >
                  {loading.test['razorpay-transaction'] ? (
                    <RefreshCw className="h-4 w-4 animate-spin" />
                  ) : (
                    <CreditCard className="h-4 w-4" />
                  )}
                  <span>Test Transaction (₹1)</span>
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Shiprocket Configuration */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="p-6 border-b">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                <Truck className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Shiprocket</h2>
                <p className="text-sm text-gray-600">Shipping and logistics integration</p>
              </div>
            </div>

            <div className="flex items-center space-x-4">
              {/* Status Badge */}
              <div className="flex items-center space-x-2">
                {apiSettings.shiprocket.configured ? (
                  <div className="flex items-center space-x-1">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span className="text-sm text-green-600 font-medium">Connected</span>
                  </div>
                ) : (
                  <div className="flex items-center space-x-1">
                    <AlertCircle className="h-4 w-4 text-red-500" />
                    <span className="text-sm text-red-600 font-medium">Not Connected</span>
                  </div>
                )}
              </div>

              {/* Enable/Disable Toggle */}
              {apiSettings.shiprocket.configured && (
                <button
                  onClick={() => toggleProvider('shiprocket', !apiSettings.shiprocket.enabled)}
                  disabled={loading.toggle.shiprocket}
                  className={`flex items-center space-x-2 px-3 py-1 rounded-md text-sm font-medium transition-colors ${apiSettings.shiprocket.enabled
                    ? 'bg-green-100 text-green-700 hover:bg-green-200'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    } disabled:opacity-50`}
                >
                  {loading.toggle.shiprocket ? (
                    <RefreshCw className="h-3 w-3 animate-spin" />
                  ) : apiSettings.shiprocket.enabled ? (
                    <Power className="h-3 w-3" />
                  ) : (
                    <PowerOff className="h-3 w-3" />
                  )}
                  <span>{apiSettings.shiprocket.enabled ? 'Enabled' : 'Disabled'}</span>
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="p-6 space-y-4">
          {/* Current Configuration Display */}
          {apiSettings.shiprocket.configured && (
            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-700">Email:</span>
                <span className="text-sm text-gray-900">{apiSettings.shiprocket.email}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-700">Password:</span>
                <span className="text-sm text-gray-900 font-mono">{apiSettings.shiprocket.password_masked}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-700">Last Verified:</span>
                <span className="text-sm text-gray-600">{formatDateTime(apiSettings.shiprocket.last_verified)}</span>
              </div>
              {apiSettings.shiprocket.token_expires_at && (
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-700">Token Expires:</span>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-600">{formatDateTime(apiSettings.shiprocket.token_expires_at)}</span>
                    {isTokenExpiringSoon(apiSettings.shiprocket.token_expires_at) && (
                      <div className="flex items-center space-x-1">
                        <Clock className="h-3 w-3 text-yellow-500" />
                        <span className="text-xs text-yellow-600 font-medium">Expiring Soon</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Configuration Form */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Shiprocket Email *
              </label>
              <input
                type="email"
                value={formData.shiprocket.email}
                onChange={(e) => handleInputChange('shiprocket', 'email', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                placeholder="your@email.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Shiprocket Password *
              </label>
              <div className="relative">
                <input
                  type={showPasswords.shiprocket_password ? 'text' : 'password'}
                  value={formData.shiprocket.password}
                  onChange={(e) => handleInputChange('shiprocket', 'password', e.target.value)}
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="Your password"
                />
                <button
                  type="button"
                  onClick={() => setShowPasswords(prev => ({
                    ...prev,
                    shiprocket_password: !prev.shiprocket_password
                  }))}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPasswords.shiprocket_password ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
          </div>

          {/* Options */}
          <div className="flex items-center space-x-6">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.shiprocket.is_enabled}
                onChange={(e) => handleInputChange('shiprocket', 'is_enabled', e.target.checked)}
                className="rounded border-gray-300 text-orange-600 focus:ring-orange-500 mr-2"
              />
              <span className="text-sm text-gray-700">Enable after saving</span>
            </label>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-3">
            <button
              onClick={saveShiprocketConfig}
              disabled={loading.shiprocket}
              className="flex items-center space-x-2 bg-orange-600 text-white px-4 py-2 rounded-md hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading.shiprocket ? (
                <RefreshCw className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              <span>Save Configuration</span>
            </button>

            {apiSettings.shiprocket.configured && (
              <button
                onClick={() => testConnection('shiprocket')}
                disabled={loading.test.shiprocket}
                className="flex items-center space-x-2 bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 disabled:opacity-50"
              >
                {loading.test.shiprocket ? (
                  <RefreshCw className="h-4 w-4 animate-spin" />
                ) : (
                  <TestTube className="h-4 w-4" />
                )}
                <span>Test Connection</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Security Notice */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <Shield className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="text-sm font-medium text-blue-900 mb-1">Security Information</h3>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• All API credentials are encrypted and stored securely</li>
              <li>• Credentials are automatically tested when saved</li>
              <li>• Shiprocket tokens are automatically refreshed before expiry</li>
              <li>• Only masked versions of credentials are displayed</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default API;